/**
 * Paid-ad-copy field limits, per platform.
 *
 * Same shape discipline as `platform-specs.ts`, extended with a provenance
 * field per limit. `docs/planning/PAID-AD-COPY-SPEC-2026-08-08.md` §3 is the
 * source for Meta. Google RSA limits were subsequently verified against
 * Google's official Responsive Search Ads guide and current Google Ads field
 * reference, so those fields carry real hard maxima. LinkedIn's numbers were
 * verified 2026-08-11 against LinkedIn's own "Single image ads advertising
 * specifications" help page (closing spec §6 Q5) and now carry real
 * hard/advise pairs too. Meta's numbers remain advisory-only (they-assert)
 * until Meta's own docs are captured. `checkAdContract`/`adviseAdContract` in
 * `src/lib/content-doctrine/ad-contract.ts` are the only readers that should
 * import this file; the provenance string travels with the number so an
 * unverified limit is visible at the call site, not just in this comment.
 */

export type AdPlatformId = 'meta' | 'linkedin' | 'google_rsa';

export interface AdFieldSpec {
  /** Hard character/word ceiling — enforced as a FAIL. Null = none confirmed. */
  hardMax: number | null;
  /** Soft ceiling from the incumbent's own prose — enforced as a WARN. */
  adviseMax: number | null;
  /** Unit the number is counted in. */
  unit: 'chars' | 'words';
  /** Where the number came from, and whether it has ever been verified
   *  against the platform's own docs (they-assert vs platform-doc). */
  provenance: string;
}

export interface MetaAdSpec {
  primaryText: AdFieldSpec;
  headline: AdFieldSpec;
  headlinePreferred: AdFieldSpec;
  description: AdFieldSpec;
  hookFold: AdFieldSpec;
}

export interface LinkedInCopySpecs {
  commentary: AdFieldSpec;
  headline: AdFieldSpec;
  description: AdFieldSpec;
}

export interface GoogleRsaAdSpec {
  headline: AdFieldSpec;
  headlineCount: { min: number; max: number; provenance: string };
  description: AdFieldSpec;
  descriptionCount: { min: number; max: number; provenance: string };
  path: AdFieldSpec;
  pathCount: { min: number; max: number; provenance: string };
}

const THEY_ASSERT_META =
  'they-assert: docs/research/competitors/marketer-com/skills/ad-copy-meta.md — never verified against developers.facebook.com. See spec §6 Q3.';

export const AD_SPECS: {
  meta: MetaAdSpec;
  linkedin: LinkedInCopySpecs;
  /** Verified field/count contract. Generation is still intentionally not
   *  wired into `generate_ad_copy`; the schema is ready for that later tool
   *  capability without weakening the live Meta path. */
  google_rsa: GoogleRsaAdSpec;
} = {
  meta: {
    primaryText: {
      hardMax: null,
      adviseMax: null,
      unit: 'chars',
      provenance:
        "UNVERIFIED — no hard API max found anywhere in the spec's three research passes. Never enforced, not even as advisory.",
    },
    hookFold: {
      hardMax: null,
      adviseMax: 125,
      unit: 'chars',
      provenance: `${THEY_ASSERT_META} "~125 chars visible before 'See more' on mobile feed." The hook must land inside this window; the rest of primary text may run long.`,
    },
    headline: {
      hardMax: null,
      adviseMax: 40,
      unit: 'chars',
      provenance: THEY_ASSERT_META,
    },
    headlinePreferred: {
      hardMax: null,
      adviseMax: 27,
      unit: 'chars',
      provenance: `${THEY_ASSERT_META} "≤ ~27 to avoid truncation on some placements."`,
    },
    description: {
      hardMax: null,
      adviseMax: 30,
      unit: 'chars',
      provenance: `${THEY_ASSERT_META} "≤ ~30 chars to render reliably; treat as optional reinforcement, never carry critical info."`,
    },
  },
  linkedin: {
    // Verified 2026-08-11 against LinkedIn's own "Single image ads
    // advertising specifications" help article — the current live doc for
    // Sponsored Content copy fields (superseding the three failed fetch
    // attempts and the grep-confirmed absence recorded in spec §6 Q5 /
    // 01-RESEARCH-linkedin-ads-api.md). hardMax is LinkedIn's own storage
    // ceiling (copy accepted up to this length); adviseMax is LinkedIn's own
    // recommended length to avoid truncation on some devices/placements —
    // both numbers are platform-doc, not they-assert.
    commentary: {
      hardMax: 3000,
      adviseMax: 150,
      unit: 'chars',
      provenance:
        'platform-doc: "Sponsored Content and Direct Sponsored Content advertising specifications" / "Single image ads advertising specifications", LinkedIn Marketing Solutions Help (linkedin.com/help/lms/answer/a426534), verified 2026-08-11: "Use up to 150 characters including spaces, emojis, and punctuation to avoid truncation (3,000 character maximum)."',
    },
    headline: {
      hardMax: 200,
      adviseMax: 70,
      unit: 'chars',
      provenance:
        'platform-doc: same source as commentary, verified 2026-08-11: "Use up to 70 characters to avoid truncation (200 character maximum)."',
    },
    description: {
      hardMax: 300,
      adviseMax: 100,
      unit: 'chars',
      provenance:
        'platform-doc: same source as commentary, verified 2026-08-11: "Use 100 characters to avoid truncation (300 character maximum)."',
    },
  },
  google_rsa: {
    headline: {
      hardMax: 30,
      adviseMax: 30,
      unit: 'chars',
      provenance:
        'platform-doc: Google Ads Help responsive search ad text asset specifications; Google Ads API Responsive Search Ads guide, verified 2026-08-08.',
    },
    headlineCount: {
      min: 3,
      max: 15,
      provenance:
        'platform-doc: Google Ads API Responsive Search Ads guide requires three or more headlines; Google Ads API ad-types guide permits up to 15.',
    },
    description: {
      hardMax: 90,
      adviseMax: 90,
      unit: 'chars',
      provenance:
        'platform-doc: Google Ads Help responsive search ad text asset specifications, verified 2026-08-08.',
    },
    descriptionCount: {
      min: 2,
      max: 4,
      provenance:
        'platform-doc: Google Ads API Responsive Search Ads guide requires two or more descriptions; Google Ads API ad-types guide permits up to 4.',
    },
    path: {
      hardMax: 15,
      adviseMax: 15,
      unit: 'chars',
      provenance: 'platform-doc: Google Ads Help About responsive search ads, verified 2026-08-08.',
    },
    pathCount: {
      min: 0,
      max: 2,
      provenance:
        'platform-doc: Google Ads API ResponsiveSearchAdInfo defines optional path1/path2; path2 requires path1.',
    },
  },
};
